/**
 * FmNumberField - DS-branded numeric input (wraps AntD InputNumber).
 */
import React from 'react';
import { InputNumber } from 'antd';
import type { InputNumberProps } from 'antd';
import { FmFormField, type FmFormFieldMetaProps, type FmNumberFieldDescriptor } from './FmForm';

export interface FmNumberFieldProps extends FmFormFieldMetaProps {
  descriptor?: FmNumberFieldDescriptor;
  id?: string;
  value?: number | null;
  defaultValue?: number;
  onChange?: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  placeholder?: string;
  size?: 'small' | 'middle' | 'large';
  full?: boolean;
  disabled?: boolean;
  parser?: InputNumberProps['parser'];
  formatter?: InputNumberProps['formatter'];
  stringMode?: boolean;
}

const toDescriptor = (props: FmNumberFieldProps): FmNumberFieldDescriptor => ({
  kind: 'number',
  name: props.name ?? '',
  label: String(props.label ?? ''),
  helper: props.helper ? String(props.helper) : undefined,
  error: props.error ? String(props.error) : undefined,
  required: props.required,
  unit: props.unit ? String(props.unit) : undefined,
  basisNote: props.basisNote ? String(props.basisNote) : undefined,
  disabledReason: props.disabledReason ? String(props.disabledReason) : undefined,
  lockedReason: props.lockedReason ? String(props.lockedReason) : undefined,
  validation: props.validation,
  intent: props.intent,
  density: props.density,
  layoutPreset: props.layoutPreset,
  disabled: props.disabled,
  id: props.id,
  placeholder: props.placeholder,
  size: props.size,
  testId: props['data-testid'],
  value: props.value,
  defaultValue: props.defaultValue,
  onChange: props.onChange,
  min: props.min,
  max: props.max,
  step: props.step,
  precision: props.precision,
  parser: props.parser,
  formatter: props.formatter,
  stringMode: props.stringMode,
});

export const FmNumberField: React.FC<FmNumberFieldProps> = (props) => {
  if (props.descriptor) {
    return <FmFormField descriptor={props.descriptor} />;
  }

  if (props.name && props.label) {
    return <FmFormField descriptor={toDescriptor(props)} />;
  }

  return (
    <InputNumber
      id={props.id}
      value={props.value}
      defaultValue={props.defaultValue}
      onChange={(nextValue) => props.onChange?.(typeof nextValue === 'number' ? nextValue : null)}
      min={props.min}
      max={props.max}
      step={props.step ?? 1}
      precision={props.precision}
      placeholder={props.placeholder}
      size={props.size ?? 'middle'}
      disabled={props.disabled}
      parser={props.parser}
      formatter={props.formatter}
      stringMode={props.stringMode}
      controls={false}
      className={props.full ? 'fm-number-field--full' : undefined}
      data-testid={props['data-testid']}
    />
  );
};

export default FmNumberField;
